import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordChecklist, passwordValid, generatePassword, usePasswordPolicy } from "../components/PasswordStrength";
import { Switch } from "../components/ui/switch";
import { usePreferences } from "../context/PreferencesContext";
import { ShieldCheck, Key, LockKey, ArrowsClockwise, Copy, CheckCircle, Sliders } from "@phosphor-icons/react";

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const policy = usePasswordPolicy();

  const submit = async () => {
    if (next !== confirm) return toast.error("New passwords don't match.");
    if (!passwordValid(next, policy)) return toast.error("New password doesn't meet the complexity rules.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      setCur(""); setNext(""); setConfirm("");
      toast.success("Password changed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-5 max-w-xl" data-testid="change-password-card">
      <div className="font-display font-bold text-sm mb-1 flex items-center gap-2"><Key size={16} />Change Password</div>
      <p className="text-sm text-muted-foreground mb-4">Use at least 14 characters with a mix of upper/lowercase, a number and a special character.</p>
      <div className="space-y-3">
        <div><Label className="text-xs">Current password</Label>
          <Input data-testid="cur-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-none mt-1" /></div>
        <div><Label className="text-xs">New password</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid="new-password" type="text" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-none font-mono" />
            <Button type="button" variant="outline" className="rounded-none whitespace-nowrap" onClick={() => { const g = generatePassword(); setNext(g); setConfirm(g); }}>
              <ArrowsClockwise size={14} className="mr-1" />Generate
            </Button>
          </div>
        </div>
        <div><Label className="text-xs">Confirm new password</Label>
          <Input data-testid="confirm-password" type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none mt-1 font-mono" /></div>
        <PasswordChecklist pw={next} className="pt-1" />
        <Button data-testid="save-password-btn" onClick={submit} disabled={busy || !cur || !passwordValid(next, policy) || next !== confirm}
          className="rounded-none bg-primary hover:bg-primary/90">{busy ? "Saving…" : "Update password"}</Button>
      </div>
    </div>
  );
}

function MfaCard() {
  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null); // {secret, qr, otpauth_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); setBackupCodes(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code });
      setBackupCodes(data.backup_codes); setSetup(null); setCode("");
      await refreshUser();
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    if (!window.confirm("Disable two-factor authentication?")) return;
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      setDisableCode(""); await refreshUser();
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copyCodes = () => { navigator.clipboard?.writeText((backupCodes || []).join("\n")); toast.success("Backup codes copied"); };

  return (
    <div className="border border-border bg-card p-5 max-w-xl" data-testid="mfa-card">
      <div className="font-display font-bold text-sm mb-1 flex items-center gap-2"><ShieldCheck size={16} />Two-Factor Authentication (TOTP)</div>

      {user?.mfa_enabled ? (
        <div data-testid="mfa-enabled-state">
          <div className="flex items-center gap-2 text-green-700 text-sm font-semibold mb-3"><CheckCircle size={16} weight="fill" />Enabled — your account is protected by an authenticator app.</div>
          <p className="text-sm text-muted-foreground mb-3">To turn it off, enter a current authenticator or backup code.</p>
          <div className="flex gap-2 max-w-sm">
            <Input data-testid="mfa-disable-code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456 or backup code" className="rounded-none font-mono" />
            <Button data-testid="mfa-disable-btn" onClick={disable} disabled={busy || !disableCode} variant="outline" className="rounded-none border-red-300 text-red-600 hover:bg-red-50">Disable</Button>
          </div>
        </div>
      ) : backupCodes ? (
        <div data-testid="mfa-backup-codes">
          <div className="flex items-center gap-2 text-green-700 text-sm font-semibold mb-2"><CheckCircle size={16} weight="fill" />MFA enabled. Save your backup codes!</div>
          <p className="text-sm text-muted-foreground mb-3">Each code works once if you lose your device. Store them somewhere safe — they won't be shown again.</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm border border-border p-3 bg-secondary/30">
            {backupCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <Button data-testid="copy-backup-btn" onClick={copyCodes} variant="outline" className="rounded-none mt-3"><Copy size={14} className="mr-1" />Copy codes</Button>
        </div>
      ) : setup ? (
        <div data-testid="mfa-setup-state">
          <p className="text-sm text-muted-foreground mb-3">Scan this QR code with Google Authenticator, Authy, 1Password, etc. — then enter the 6-digit code to confirm.</p>
          <img src={setup.qr} alt="MFA QR" className="h-44 w-44 border border-border" data-testid="mfa-qr" />
          <p className="text-xs text-muted-foreground mt-2">Can't scan? Enter this key manually: <span className="font-mono break-all">{setup.secret}</span></p>
          <div className="flex gap-2 max-w-sm mt-3">
            <Input data-testid="mfa-enable-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="rounded-none font-mono tracking-widest" />
            <Button data-testid="mfa-enable-btn" onClick={enable} disabled={busy || !code} className="rounded-none bg-primary hover:bg-primary/90">Verify & enable</Button>
          </div>
          <button onClick={() => setSetup(null)} className="text-xs underline text-muted-foreground mt-3">Cancel</button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2"><LockKey size={16} />Add a second layer of security to your sign-in with an authenticator app.</p>
          <Button data-testid="mfa-setup-btn" onClick={startSetup} disabled={busy} className="rounded-none bg-primary hover:bg-primary/90">Set up authenticator app</Button>
        </div>
      )}
    </div>
  );
}

function PreferencesCard() {
  const { prefs, setPref } = usePreferences();
  return (
    <div className="border border-border bg-card p-5 max-w-xl" data-testid="preferences-card">
      <div className="font-display font-bold text-sm mb-1 flex items-center gap-2"><Sliders size={16} />Preferences</div>
      <p className="text-sm text-muted-foreground mb-4">Personalize how the workspace behaves for you. Saved to this browser.</p>
      <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
        <div>
          <Label className="text-sm font-semibold">Dark mode</Label>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-md">Switch the workspace to a dark color theme. Easier on the eyes in low light.</p>
        </div>
        <Switch data-testid="pref-dark-mode" checked={prefs.theme === "dark"} onCheckedChange={(v) => setPref("theme", v ? "dark" : "light")} />
      </div>
      <div className="flex items-start justify-between gap-4 border-t border-border pt-4 mt-4">
        <div>
          <Label className="text-sm font-semibold">Requirement hover tooltips</Label>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-md">Show the full requirement text in a hover card when you point at a requirement number (e.g. CIP-007-6 R1). Turn off if you prefer just the numbers.</p>
        </div>
        <Switch data-testid="pref-hover-tooltips" checked={prefs.hoverTooltips} onCheckedChange={(v) => setPref("hoverTooltips", v)} />
      </div>
    </div>
  );
}

export default function Account() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader title="Account Security" subtitle="Manage your password and two-factor authentication" />
      <div className="p-8 space-y-6">
        <div className="border border-border bg-card p-5 max-w-xl" data-testid="account-identity-card">
          <div className="font-display font-bold text-sm mb-3">Your identity</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="overline text-xs text-muted-foreground">Name</div><div className="font-medium mt-0.5">{user?.name}</div></div>
            <div><div className="overline text-xs text-muted-foreground">Email</div><div className="font-medium mt-0.5 break-all">{user?.email}</div></div>
            <div><div className="overline text-xs text-muted-foreground">Claro ID</div><div className="font-mono mt-0.5" data-testid="account-claro-id">{user?.claro_id || "—"}</div></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Your Claro ID is a permanent identifier stamped on documents you author. To change your name or email, contact an administrator.</p>
        </div>
        <PreferencesCard />
        <ChangePassword />
        <MfaCard />
      </div>
    </div>
  );
}
